// src/pages/OrderConfirmationPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { API_CONFIG } from '../utils/apiConfig';
import { showToast } from "../utils/helpers";

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [pin, setPin] = useState(Array(6).fill(""));
  const [customerEmail, setCustomerEmail] = useState(""); // Fixed variable name
  const [orderInfo, setOrderInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);

  // Auto-focus first input on load
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    
    // Fetch order details if orderId is provided
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await api.get(`${API_CONFIG.ORDERS.ORDER_TRACK}/${orderId}`);
      if (response.data.success) {
        setOrderInfo(response.data.order);
        // Pre-fill email if available from order info
        if (response.data.order.customer_email) {
          setCustomerEmail(response.data.order.customer_email);
        }
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      // Continue even if order details can't be fetched
    }
  };

  const handleEmailChange = (value) => {
    setCustomerEmail(value);
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handlePinChange = (index, value) => {
    // Allow only digits
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
    
    // Auto-submit if all digits are entered AND email is provided
    if (newPin.every(digit => digit !== "") && index === 5 && customerEmail) {
      handleSubmit();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setPin(digits);
      
      // Focus the last input
      if (inputRefs.current[5]) {
        inputRefs.current[5].focus();
      }
      
      // Auto-submit after paste if email is provided
      if (customerEmail) {
        setTimeout(() => handleSubmit(), 100);
      }
    }
  };

  const handleSubmit = async () => {
    const confirmationCode = pin.join("");

    if (confirmationCode.length !== 6) {
      setError("Please enter a complete 6-digit code");
      return;
    }

    if (!customerEmail) {
      setError("Please enter your email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await api.post(`${API_CONFIG.ORDERS.CONFIRM_DELIVERY}/${orderId || orderInfo?.order_id}/confirm-delivery`, {
        confirmationCode,
        email: customerEmail // Using correct parameter name for backend
      });

      if (response.data.success) {
        showToast("Delivery confirmed successfully! Payment has been released to the seller.", "success");
        
        // Redirect to order tracking page or show success message
        setTimeout(() => {
          navigate(`/order/${orderId || orderInfo?.order_id}`);
        }, 2000);
      } else {
        setError(response.data?.error || "Invalid confirmation code");
      }
    } catch (error) {
      console.error("Error confirming delivery:", error);
      setError(error.response?.data?.error || "Failed to confirm delivery. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    if (!customerEmail) {
      setError("Please enter your email address first");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const response = await api.post(`${API_CONFIG.ORDERS.RESEND_CODE}/${orderId || orderInfo?.order_id}/resend-code`, {
        email: customerEmail // Include email in the request
      });
      
      if (response.data.success) {
        showToast("New confirmation code sent to your email!", "success");
      } else {
        setError(response.data.message || "Failed to resend code. Please try again.");
      }
    } catch (error) {
      console.error("Error resending code:", error);
      setError(error.response?.data?.message || "Failed to resend code. Please try again.");
    }
  };

  // Check if form is complete (both email and 6-digit code)
  const isFormComplete = customerEmail && pin.join("").length === 6;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Confirm Delivery</h2>
            <p className="text-gray-600 mb-6">
              Enter the 6-digit confirmation code sent to your email
            </p>
          </div>

          {orderInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">Order Details</h3>
              <p className="text-sm text-blue-700">
                Order #: <strong>{orderInfo.order_id}</strong>
              </p>
              {orderInfo.items && orderInfo.items.length > 0 && (
                <p className="text-sm text-blue-700 mt-1">
                  Contains: {orderInfo.items.length} item{orderInfo.items.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}

          {/* Email Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Your Email Address *
            </label>
            <input 
              type="email" 
              required 
              disabled={isSubmitting}
              value={customerEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              placeholder="johndoe@example.com" 
            />
          </div>

          {/* PIN Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              6-Digit Confirmation Code *
            </label>
            <div className="flex justify-between space-x-2">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  disabled={isSubmitting || !customerEmail}
                />
              ))}
            </div>

            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>

          <div className="mb-6">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormComplete}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Confirming...
                </>
              ) : (
                "Confirm Delivery"
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={resendCode}
              disabled={!customerEmail}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Resend confirmation code
            </button>
          </div>
        </div>

        {/* Information Section */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Why confirm delivery?</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>• Confirming delivery releases payment to the seller</p>
                <p>• Helps maintain the integrity of our escrow system</p>
                <p>• Ensures you received your order as expected</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;